import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiCalendar,
  FiCheckCircle,
  FiCompass,
  FiClock,
  FiGlobe,
  FiMapPin,
  FiMessageCircle,
  FiMinusCircle,
  FiNavigation,
  FiSend,
  FiShield,
  FiTag,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { safariTrips } from "../data/safariTrips";

const formatAccommodation = (accommodation) => {
  if (!accommodation) return "To be advised";
  if (typeof accommodation === "object") return null;
  return accommodation;
};

const getDuration = (trip) => {
  if (trip.durationLabel) return trip.durationLabel;

  const match = trip.title?.match(/\d+\s*(Days|Nights)/i);
  if (match) return match[0];

  if (Array.isArray(trip.itinerary) && trip.itinerary.length > 0) {
    return `${trip.itinerary.length} Days`;
  }

  return "Custom Trip";
};

const getDestination = (trip) => {
  if (trip.destination) return trip.destination;
  if (Array.isArray(trip.countries) && trip.countries.length > 0) {
    return trip.countries.join(" & ");
  }
  return "East Africa";
};

const getPrice = (trip) => {
  const amount = trip.price?.discounted || trip.price?.original;
  if (!amount) return "Quote on request";
  return `${trip.price?.currency || "USD"} ${amount.toLocaleString()}`;
};

const getGuestLabel = (trip) => {
  if (trip.minimumGuests) return `Minimum ${trip.minimumGuests} Guests`;
  return "Flexible group size";
};

const getAccommodationSummary = (trip) => {
  if (trip.accommodationSummary) return trip.accommodationSummary;
  return "As listed in the itinerary";
};

const getTransport = (trip) => trip.transport || "Safari vehicle";

const InfoTile = ({ label, value, icon: Icon }) => (
  <div className="min-w-0 py-4 md:px-5">
    <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#C88804]">
      <Icon className="shrink-0" />
      {label}
    </p>
    <p className="break-words text-sm font-semibold leading-6 text-[#082f1f]">
      {value}
    </p>
  </div>
);

const ListPanel = ({ title, items, icon: Icon, tone = "positive" }) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  const accent = tone === "positive" ? "text-[#0f7a4f]" : "text-[#C88804]";

  return (
    <div className="rounded-sm border border-[#eadfbe] bg-white p-6 shadow-lg shadow-black/5">
      <h3 className="mb-4 flex items-center gap-2 text-xl font-extrabold text-[#082f1f]">
        <Icon className={accent} />
        {title}
      </h3>
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item} className="flex gap-3 text-sm leading-7 text-[#5d7168]">
            <Icon className={`mt-1 shrink-0 ${accent}`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

const DetailedSafariTrip = ({ tripId }) => {
  const safariTrip = safariTrips.find((trip) => trip.id === tripId);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    guests: 1,
    message: "",
  });

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    alert(`Thank you, ${formData.name}! Your safari request has been submitted.`);
    setFormData({ name: "", email: "", phone: "", guests: 1, message: "" });
    setShowBookingForm(false);
  };

  if (!safariTrip) {
    return (
      <section className="flex min-h-[60vh] items-center justify-center bg-[#f6f1e7] px-4 pt-28">
        <div className="rounded-sm bg-white p-8 text-center shadow-xl">
          <h1 className="text-2xl font-extrabold text-[#082f1f]">Safari trip not found</h1>
          <p className="mt-2 text-[#5d7168]">Please choose another safari package.</p>
        </div>
      </section>
    );
  }

  return (
    <div className="bg-[#f6f1e7] font-[Poppins] text-[#082f1f]">
      <section className="relative min-h-[72vh] overflow-hidden pt-28 text-white">
        <img
          src={safariTrip.image}
          alt={`${safariTrip.title} safari`}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#03140d]/95 via-[#03140d]/70 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#f6f1e7] to-transparent" />

        <div className="relative z-10 mx-auto flex min-h-[72vh] max-w-7xl items-center px-4 pb-20 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <div className="mb-5 inline-flex items-center gap-2 border-l-4 border-[#F5B731] bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#F5B731] backdrop-blur">
              Safari Package
            </div>
            <h1 className="text-4xl font-extrabold uppercase leading-tight tracking-normal sm:text-5xl lg:text-6xl">
              {safariTrip.title}
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-white/80 sm:text-lg">
              {safariTrip.description}
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 rounded-sm bg-[#F5B731] px-4 py-3 text-sm font-bold text-[#082f1f]">
                <FiClock />
                {getDuration(safariTrip)}
              </span>
              <span className="inline-flex items-center gap-2 rounded-sm bg-white/10 px-4 py-3 text-sm font-semibold text-white backdrop-blur">
                <FiMapPin className="text-[#F5B731]" />
                {getDestination(safariTrip)}
              </span>
              <span className="inline-flex items-center gap-2 rounded-sm bg-white/10 px-4 py-3 text-sm font-semibold text-white backdrop-blur">
                <FiTag className="text-[#F5B731]" />
                {getPrice(safariTrip)}
              </span>
              <span className="inline-flex items-center gap-2 rounded-sm bg-white/10 px-4 py-3 text-sm font-semibold text-white backdrop-blur">
                <FiUsers className="text-[#F5B731]" />
                {getGuestLabel(safariTrip)}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      <main className="mx-auto grid max-w-7xl gap-8 px-4 pb-20 sm:px-6 lg:grid-cols-[1fr_360px] lg:px-8">
        <div className="space-y-8">
          <section className="rounded-sm bg-white p-6 shadow-xl shadow-black/5">
            <div className="grid divide-y divide-[#eadfbe] md:grid-cols-2 md:divide-x md:divide-y-0 xl:grid-cols-4">
              <InfoTile
                label="Start Location"
                value={safariTrip.startLocation || "Flexible"}
                icon={FiMapPin}
              />
              <InfoTile
                label="End Location"
                value={safariTrip.endLocation || "Flexible"}
                icon={FiNavigation}
              />
              <InfoTile
                label="Guests"
                value={getGuestLabel(safariTrip)}
                icon={FiUsers}
              />
              <InfoTile
                label="Transport"
                value={getTransport(safariTrip)}
                icon={FiCompass}
              />
            </div>

            {Array.isArray(safariTrip.typeOfSafari) && (
              <div className="mt-6 flex flex-wrap gap-2">
                {safariTrip.typeOfSafari.map((type) => (
                  <span
                    key={type}
                    className="rounded-sm bg-[#082f1f] px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] text-white"
                  >
                    {type}
                  </span>
                ))}
              </div>
            )}
          </section>

          {Array.isArray(safariTrip.highlights) && safariTrip.highlights.length > 0 && (
            <section className="rounded-sm bg-[#082f1f] p-6 text-white shadow-xl shadow-black/10">
              <div className="mb-6 flex items-center gap-3">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-sm bg-[#F5B731] text-xl text-[#082f1f]">
                  <FiGlobe />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#F5B731]">
                    Package Highlights
                  </p>
                  <h2 className="mt-1 text-2xl font-extrabold uppercase">
                    What Makes This Trip Stand Out
                  </h2>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {safariTrip.highlights.map((highlight) => (
                  <div
                    key={highlight}
                    className="flex gap-3 border border-white/10 bg-white/5 p-4 text-sm leading-7 text-white/80"
                  >
                    <FiCheckCircle className="mt-1 shrink-0 text-[#F5B731]" />
                    {highlight}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-sm bg-white p-6 shadow-xl shadow-black/5">
            <div className="mb-8">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#C88804]">
                Day By Day
              </p>
              <h2 className="mt-2 text-3xl font-extrabold uppercase text-[#082f1f]">
                Safari Itinerary
              </h2>
            </div>

            <div className="space-y-8">
              {safariTrip.itinerary.map((day, index) => (
                <motion.article
                  key={`${day.day}-${day.location}`}
                  initial={{ opacity: 0, y: 26 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.18 }}
                  transition={{ duration: 0.5, delay: index * 0.04 }}
                  className="relative border-l border-[#d9cda9] pl-7"
                >
                  <div className="absolute -left-[18px] top-0 grid h-9 w-9 place-items-center rounded-sm bg-[#F5B731] text-sm font-extrabold text-[#082f1f] shadow-md">
                    {day.day}
                  </div>

                  <div className="rounded-sm border border-[#eadfbe] bg-[#fbf8f0] p-5">
                    <h3 className="text-2xl font-extrabold text-[#082f1f]">
                      Day {day.day}: {day.location}
                    </h3>
                    <p className="mt-3 flex items-start gap-2 text-sm font-semibold text-[#C88804]">
                      <FiCalendar className="mt-1 shrink-0" />
                      {day.activity}
                    </p>
                    <p className="mt-4 text-sm leading-7 text-[#5d7168]">{day.details}</p>

                    <div className="mt-5 rounded-sm bg-white p-4 text-sm text-[#5d7168]">
                      <p className="mb-2 font-bold text-[#082f1f]">Accommodation</p>
                      {typeof day.accommodation === "object" && day.accommodation !== null ? (
                        <div className="grid gap-2 sm:grid-cols-3">
                          {Object.entries(day.accommodation).map(([tier, name]) => (
                            <div key={tier} className="rounded-sm bg-[#f6f1e7] p-3">
                              <span className="block text-xs font-bold uppercase text-[#C88804]">
                                {tier}
                              </span>
                              <span>{name}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span>{formatAccommodation(day.accommodation)}</span>
                      )}
                      {day.mealPlan && (
                        <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#315245]">
                          Meal Plan: {day.mealPlan}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>

          <div className="grid gap-6 lg:grid-cols-2">
            <ListPanel title="Included" items={safariTrip.includes} icon={FiCheckCircle} />
            <ListPanel title="Excluded" items={safariTrip.excludes} icon={FiMinusCircle} tone="warning" />
          </div>

          <ListPanel title="Travel Notes" items={safariTrip.notes} icon={FiShield} />
        </div>

        <aside className="lg:sticky lg:top-28 lg:h-fit">
          <div className="rounded-sm bg-white p-6 shadow-2xl shadow-black/10">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C88804]">
              Enquire Now
            </p>
            <h2 className="mt-2 text-2xl font-extrabold text-[#082f1f]">
              Plan This Safari
            </h2>
            <p className="mt-3 text-sm leading-7 text-[#5d7168]">
              Ask about availability, permits, lodges, seasonal pricing, and
              custom adjustments for this itinerary.
            </p>

            <div className="mt-6 space-y-4 border-y border-[#eadfbe] py-5">
              <div className="grid grid-cols-[0.85fr_1.15fr] gap-3 text-sm">
                <span className="text-[#5d7168]">Duration</span>
                <strong className="text-right">{getDuration(safariTrip)}</strong>
              </div>
              <div className="grid grid-cols-[0.85fr_1.15fr] gap-3 text-sm">
                <span className="text-[#5d7168]">Destination</span>
                <strong className="text-right">{getDestination(safariTrip)}</strong>
              </div>
              <div className="grid grid-cols-[0.85fr_1.15fr] gap-3 text-sm">
                <span className="text-[#5d7168]">Guests</span>
                <strong className="text-right">{getGuestLabel(safariTrip)}</strong>
              </div>
              <div className="grid grid-cols-[0.85fr_1.15fr] gap-3 text-sm">
                <span className="text-[#5d7168]">Transport</span>
                <strong className="text-right">{getTransport(safariTrip)}</strong>
              </div>
              <div className="grid grid-cols-[0.85fr_1.15fr] gap-3 text-sm">
                <span className="text-[#5d7168]">Accommodation</span>
                <strong className="text-right">{getAccommodationSummary(safariTrip)}</strong>
              </div>
              <div className="grid grid-cols-[0.85fr_1.15fr] gap-3 text-sm">
                <span className="text-[#5d7168]">Price</span>
                <strong className="text-right">{getPrice(safariTrip)}</strong>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowBookingForm(true)}
              className="mt-6 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-sm bg-[#082f1f] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#F5B731] hover:text-[#082f1f]"
            >
              Request Booking
              <FiSend />
            </button>

            <div className="mt-4 flex gap-3 rounded-sm bg-[#f6f1e7] p-4 text-sm leading-6 text-[#5d7168]">
              <FiMessageCircle className="mt-1 shrink-0 text-[#C88804]" />
              We will reply with realistic routing, permit advice, and a quote.
            </div>
          </div>
        </aside>
      </main>

      <AnimatePresence>
        {showBookingForm && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative w-full max-w-lg rounded-sm bg-white p-6 shadow-2xl md:p-8"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.96 }}
              transition={{ duration: 0.25 }}
            >
              <button
                type="button"
                onClick={() => setShowBookingForm(false)}
                className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-sm bg-[#f6f1e7] text-[#082f1f] transition hover:bg-[#F5B731]"
                aria-label="Close booking form"
              >
                <FiX />
              </button>

              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C88804]">
                Safari Request
              </p>
              <h2 className="mt-2 pr-10 text-2xl font-extrabold text-[#082f1f]">
                Book Your Safari
              </h2>

              <form onSubmit={handleSubmit} className="mt-6 grid gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="h-12 rounded-sm border border-[#d9cda9] px-4 text-sm outline-none focus:border-[#F5B731] focus:ring-2 focus:ring-[#F5B731]/30"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-12 rounded-sm border border-[#d9cda9] px-4 text-sm outline-none focus:border-[#F5B731] focus:ring-2 focus:ring-[#F5B731]/30"
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-sm border border-[#d9cda9] px-4 text-sm outline-none focus:border-[#F5B731] focus:ring-2 focus:ring-[#F5B731]/30"
                  />
                  <input
                    type="number"
                    name="guests"
                    min="1"
                    placeholder="Guests"
                    value={formData.guests}
                    onChange={handleChange}
                    className="h-12 rounded-sm border border-[#d9cda9] px-4 text-sm outline-none focus:border-[#F5B731] focus:ring-2 focus:ring-[#F5B731]/30"
                  />
                </div>
                <textarea
                  name="message"
                  rows="4"
                  placeholder="Tell us your dates, budget, and special requests"
                  value={formData.message}
                  onChange={handleChange}
                  className="rounded-sm border border-[#d9cda9] p-4 text-sm outline-none focus:border-[#F5B731] focus:ring-2 focus:ring-[#F5B731]/30"
                />
                <button
                  type="submit"
                  className="min-h-[52px] rounded-sm bg-[#082f1f] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#F5B731] hover:text-[#082f1f]"
                >
                  Submit Request
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DetailedSafariTrip;
